import { byId, isAr, formatNumber, detectUserCurrency, trackEvent } from './calc-common.js';
import Big from 'big.js';
import {
  CURRENCIES,
  formatInvoiceAmount,
  getInvoiceTafgeet,
  initSearchableCurrency
} from './invoice-currencies.js';

const DRAFT_STORAGE_KEY = 'kazakan_invoice_draft_v2';

export const UNITS = [
  { id: 'pcs', nameAr: 'قطعة', nameEn: 'Pcs' },
  { id: 'hrs', nameAr: 'ساعة', nameEn: 'Hrs' },
  { id: 'days', nameAr: 'يوم', nameEn: 'Days' },
  { id: 'mo', nameAr: 'شهر', nameEn: 'Month' },
  { id: 'srv', nameAr: 'خدمة', nameEn: 'Service' },
  { id: 'kg', nameAr: 'كجم', nameEn: 'Kg' },
  { id: 'm', nameAr: 'متر', nameEn: 'Meter' },
  { id: 'proj', nameAr: 'مشروع', nameEn: 'Project' },
  { id: 'none', nameAr: 'بدون وحدة', nameEn: 'None' }
];

const PRESETS = {
  consulting: {
    descAr: 'استشارات مهنية متخصصة',
    descEn: 'Professional Consulting Services',
    unit: 'hrs',
    qty: 2,
    price: 400
  },
  design: {
    descAr: 'تصميم جرافيك وهوية بصرية متكاملة',
    descEn: 'Graphic & Brand Identity Design',
    unit: 'srv',
    qty: 1,
    price: 1500
  },
  dev: {
    descAr: 'تطوير وبرمجة منصة وموقع إلكتروني',
    descEn: 'Web & Software Development',
    unit: 'proj',
    qty: 1,
    price: 3500
  },
  maintenance: {
    descAr: 'دعم فني وصيانة دورية',
    descEn: 'Tech Support & Maintenance',
    unit: 'mo',
    qty: 1,
    price: 600
  },
  shipping: {
    descAr: 'خدمات شحن وتوصيل فوري',
    descEn: 'Express Shipping & Delivery',
    unit: 'srv',
    qty: 1,
    price: 120
  },
  product: {
    descAr: 'بند منتج تجاري معتمد',
    descEn: 'Standard Commercial Merchandise',
    unit: 'pcs',
    qty: 5,
    price: 95
  }
};

let items = [
  {
    id: 1,
    desc: isAr ? 'استشارات وتطوير برمجيات' : 'Software Development & Consulting',
    unit: 'hrs',
    qty: 10,
    price: 250
  },
  {
    id: 2,
    desc: isAr ? 'تصميم واجهات المستخدم وتجربة الاستخدام' : 'UI/UX Design Services',
    unit: 'srv',
    qty: 1,
    price: 1500
  }
];
let nextItemId = 3;
let sellerLogoDataUrl = '';
let currentPaperSize = 'a4';
let hasStartedTracking = false;

function markInvoiceStarted() {
  if (!hasStartedTracking) {
    hasStartedTracking = true;
    trackEvent('tool_started', 'invoice-generator');
  }
}

export function initInvoiceGenerator() {
  const container = byId('invoice-generator-app');
  if (!container) return;

  // 1. Initialize Searchable Currency Picker
  initSearchableCurrency({
    triggerBtn: byId('inv-currency-trigger'),
    popoverEl: byId('inv-currency-popover'),
    searchInput: byId('inv-currency-search-input'),
    listEl: byId('inv-currency-list'),
    hiddenInput: byId('inv-currency'),
    onSelect: () => {
      renderInvoicePreview();
      saveDraft();
    }
  });

  // 2. Paper Size Control (A4 Default)
  const paperSelect = byId('inv-paper-size');
  if (paperSelect) {
    paperSelect.addEventListener('change', () => {
      setPaperSize(paperSelect.value);
      saveDraft();
    });
  }

  // 3. Layout Flip Button (Side Swap)
  const flipBtn = byId('inv-flip-layout-btn');
  const layoutContainer = container.querySelector('.kz-calc-layout');
  if (flipBtn && layoutContainer) {
    flipBtn.addEventListener('click', () => {
      layoutContainer.classList.toggle('is-flipped');
      saveDraft();
    });
  }

  // 4. Logo upload & clear
  const logoInput = byId('inv-logo-input');
  if (logoInput) {
    logoInput.addEventListener('change', (e) => {
      if (e.target.files?.[0]) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          sellerLogoDataUrl = ev.target.result;
          const preview = byId('inv-logo-preview');
          if (preview) {
            preview.src = sellerLogoDataUrl;
            preview.style.display = 'block';
          }
          const badge = byId('inv-logo-badge');
          if (badge) badge.style.display = 'inline-flex';
          renderInvoicePreview();
          saveDraft();
        };
        reader.readAsDataURL(e.target.files[0]);
      }
    });
  }

  const removeLogoBtn = byId('inv-remove-logo');
  if (removeLogoBtn) {
    removeLogoBtn.addEventListener('click', () => {
      sellerLogoDataUrl = '';
      const preview = byId('inv-logo-preview');
      const badge = byId('inv-logo-badge');
      const fileInput = byId('inv-logo-input');
      if (fileInput) fileInput.value = '';
      if (preview) {
        preview.src = '';
        preview.style.display = 'none';
      }
      if (badge) badge.style.display = 'none';
      renderInvoicePreview();
      saveDraft();
    });
  }

  // 5. Add Blank Item Button
  const addItemBtn = byId('inv-add-item-btn');
  if (addItemBtn) {
    addItemBtn.addEventListener('click', () => {
      items.push({
        id: nextItemId++,
        desc: isAr ? 'بند جديد' : 'New Item',
        unit: 'pcs',
        qty: 1,
        price: 100
      });
      renderItemRows();
      renderInvoicePreview();
      saveDraft();
    });
  }

  // 6. Quick Service Presets Chips
  container.querySelectorAll('.inv-template-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const key = chip.dataset.preset;
      const preset = PRESETS[key];
      if (preset) {
        items.push({
          id: nextItemId++,
          desc: isAr ? preset.descAr : preset.descEn,
          unit: preset.unit,
          qty: preset.qty,
          price: preset.price
        });
        renderItemRows();
        renderInvoicePreview();
        saveDraft();
      }
    });
  });

  // 6.2 Invoice Title Preset Chips
  container.querySelectorAll('.inv-title-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const titleInput = byId('inv-title');
      if (titleInput) {
        titleInput.value = chip.dataset.title;
        const hideToggle = byId('inv-hide-title-toggle');
        if (hideToggle && hideToggle.checked) {
          hideToggle.checked = false;
          applyFieldExclusion(hideToggle);
        }
        renderInvoicePreview();
        saveDraft();
      }
    });
  });

  // 6.3 Smart Mobile Navigation Tabs (< 1060px)
  const navTabs = container.querySelectorAll('.inv-nav-tab');
  navTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const view = tab.dataset.target;
      container.setAttribute('data-mobile-view', view);
      navTabs.forEach((t) => {
        const isCurrent = t === tab;
        t.classList.toggle('active', isCurrent);
        t.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      });
      if (view === 'preview') {
        const sheet = byId('inv-printable-sheet');
        if (sheet) {
          sheet.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    });
  });

  const gotoPreviewBtn = byId('inv-mobile-goto-preview-btn');
  if (gotoPreviewBtn) {
    gotoPreviewBtn.addEventListener('click', () => {
      const previewTab = byId('inv-tab-preview');
      if (previewTab) previewTab.click();
    });
  }

  const backToEditBtn = byId('inv-mobile-back-to-edit-btn');
  if (backToEditBtn) {
    backToEditBtn.addEventListener('click', () => {
      const editTab = byId('inv-tab-edit');
      if (editTab) editTab.click();
    });
  }

  // 7. Discount Mode Segmented Pill Buttons (% vs Fixed)
  const discountTypeHidden = byId('inv-discount-type');
  const discountBtns = container.querySelectorAll('.inv-disc-btn');
  discountBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      discountBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      if (discountTypeHidden) {
        discountTypeHidden.value = btn.dataset.type || 'percent';
      }
      renderInvoicePreview();
      saveDraft();
    });
  });

  // 8. Field Exclusion Toggles
  setupFieldExclusionToggles();

  // 9. Standard Live Inputs
  const formInputs = [
    'inv-title',
    'inv-number',
    'inv-date',
    'inv-due-date',
    'inv-seller-name',
    'inv-seller-vat',
    'inv-seller-address',
    'inv-seller-phone',
    'inv-client-name',
    'inv-client-vat',
    'inv-client-address',
    'inv-vat-rate',
    'inv-discount-val',
    'inv-notes'
  ];

  formInputs.forEach((id) => {
    const el = byId(id);
    if (el) {
      el.addEventListener('input', () => {
        renderInvoicePreview();
        saveDraft();
      });
      el.addEventListener('change', () => {
        renderInvoicePreview();
        saveDraft();
      });
    }
  });

  // 10. Print / Save as PDF Button
  const printBtn = byId('inv-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      markInvoiceStarted();
      trackEvent('tool_completed', 'invoice-generator', { action: 'print_pdf' });
      window.print();
    });
  }

  window.addEventListener('beforeprint', () => {
    markInvoiceStarted();
    trackEvent('tool_completed', 'invoice-generator', { action: 'print_pdf' });
  });

  // 11. Clear / Reset Draft Button
  const resetBtn = byId('inv-reset-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      if (
        confirm(
          isAr
            ? 'هل أنت متأكد من مسح بيانات المسودة بالكامل واستعادة القيم الافتراضية؟'
            : 'Are you sure you want to reset all draft data to default?'
        )
      ) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        location.reload();
      }
    });
  }

  // Load and apply saved draft state
  loadSavedDraft();
  renderItemRows();
  renderInvoicePreview();
}

/**
 * Configure paper size for preview sheet and browser @media print
 */
function setPaperSize(sizeKey) {
  currentPaperSize = sizeKey || 'a4';
  const sheet = byId('inv-printable-sheet');
  if (sheet) {
    sheet.setAttribute('data-paper', currentPaperSize);
  }

  // Set CSS variable for @page print
  let printSize = 'A4 portrait';
  if (currentPaperSize === 'a5') printSize = 'A5 portrait';
  else if (currentPaperSize === 'letter') printSize = 'letter portrait';
  else if (currentPaperSize === 'legal') printSize = 'legal portrait';
  else if (currentPaperSize === 'thermal80') printSize = '80mm auto';

  document.documentElement.style.setProperty('--inv-print-page-size', printSize);

  const select = byId('inv-paper-size');
  if (select && select.value !== currentPaperSize) {
    select.value = currentPaperSize;
  }
}

/**
 * Setup field exclusion toggles (Hides field on invoice preview when checked)
 */
function setupFieldExclusionToggles() {
  const toggles = document.querySelectorAll('.inv-field-hide-toggle');
  toggles.forEach((toggle) => {
    toggle.addEventListener('change', () => {
      applyFieldExclusion(toggle);
      renderInvoicePreview();
      saveDraft();
    });
  });
}

function applyFieldExclusion(toggle) {
  const targetId = toggle.dataset.hideTarget;
  if (!targetId) return;

  const targetEl = byId(targetId);
  const isHidden = toggle.checked;

  if (targetEl) {
    if (isHidden) {
      targetEl.classList.add('is-hidden-inv');
    } else {
      targetEl.classList.remove('is-hidden-inv');
    }
  }

  // Visual disable on associated input if VAT or discount is completely hidden
  if (targetId === 'prev-vat-row') {
    const vatInput = byId('inv-vat-rate');
    if (vatInput) vatInput.disabled = isHidden;
  } else if (targetId === 'prev-discount-row') {
    const discInput = byId('inv-discount-val');
    if (discInput) discInput.disabled = isHidden;
  }
}

function loadSavedDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!saved) {
      const today = new Date().toISOString().split('T')[0];
      if (byId('inv-date')) byId('inv-date').value = today;
      if (byId('inv-number'))
        byId('inv-number').value = `INV-${Math.floor(1000 + Math.random() * 9000)}`;
      const detectedCur = detectUserCurrency('SAR');
      const hiddenCur = byId('inv-currency');
      if (hiddenCur) hiddenCur.value = detectedCur;
      const triggerBtn = byId('inv-currency-trigger');
      if (triggerBtn) {
        const item = CURRENCIES.find((c) => c.code === detectedCur) || CURRENCIES[0];
        const name = isAr ? item.nameAr : item.nameEn;
        triggerBtn.innerHTML = `
          <span class="inv-curr-flag">${item.flag}</span>
          <span class="inv-curr-code">${item.code}</span>
          <span class="inv-curr-name">${name} (${item.symbol})</span>
          <span class="inv-curr-arrow">▾</span>
        `;
      }
      setPaperSize('a4');
      return;
    }

    const data = JSON.parse(saved);

    // Items
    if (Array.isArray(data.items) && data.items.length > 0) {
      items = data.items;
      nextItemId = Math.max(...items.map((x) => x.id), 0) + 1;
    }

    // Logo
    if (data.sellerLogo) {
      sellerLogoDataUrl = data.sellerLogo;
      const preview = byId('inv-logo-preview');
      if (preview) {
        preview.src = sellerLogoDataUrl;
        preview.style.display = 'block';
      }
      const badge = byId('inv-logo-badge');
      if (badge) badge.style.display = 'inline-flex';
    }

    // Paper size
    if (data.paperSize) {
      setPaperSize(data.paperSize);
    } else {
      setPaperSize('a4');
    }

    // Layout flip preference
    if (data.isFlipped) {
      const layoutContainer = document.querySelector('#invoice-generator-app .kz-calc-layout');
      if (layoutContainer) layoutContainer.classList.add('is-flipped');
    }

    // Discount mode (% vs fixed)
    if (data['inv-discount-type']) {
      const type = data['inv-discount-type'];
      const discountTypeHidden = byId('inv-discount-type');
      if (discountTypeHidden) discountTypeHidden.value = type;
      const btns = document.querySelectorAll('.inv-disc-btn');
      btns.forEach((b) => {
        b.classList.toggle('active', b.dataset.type === type);
      });
    }

    // Standard inputs
    const fields = [
      'inv-title',
      'inv-number',
      'inv-date',
      'inv-due-date',
      'inv-currency',
      'inv-seller-name',
      'inv-seller-vat',
      'inv-seller-address',
      'inv-seller-phone',
      'inv-client-name',
      'inv-client-vat',
      'inv-client-address',
      'inv-vat-rate',
      'inv-discount-val',
      'inv-notes'
    ];

    fields.forEach((f) => {
      if (data[f] !== undefined && byId(f)) {
        byId(f).value = data[f];
      }
    });

    // Hidden toggles
    if (Array.isArray(data.hiddenToggles)) {
      document.querySelectorAll('.inv-field-hide-toggle').forEach((toggle) => {
        const targetId = toggle.dataset.hideTarget;
        if (targetId && data.hiddenToggles.includes(targetId)) {
          toggle.checked = true;
          applyFieldExclusion(toggle);
        }
      });
    }
  } catch (e) {
    console.warn('Could not load invoice draft', e);
  }
}

function saveDraft() {
  markInvoiceStarted();
  try {
    const fields = [
      'inv-title',
      'inv-number',
      'inv-date',
      'inv-due-date',
      'inv-currency',
      'inv-seller-name',
      'inv-seller-vat',
      'inv-seller-address',
      'inv-seller-phone',
      'inv-client-name',
      'inv-client-vat',
      'inv-client-address',
      'inv-vat-rate',
      'inv-discount-val',
      'inv-discount-type',
      'inv-notes'
    ];

    const hiddenToggles = [];
    document.querySelectorAll('.inv-field-hide-toggle:checked').forEach((chk) => {
      if (chk.dataset.hideTarget) hiddenToggles.push(chk.dataset.hideTarget);
    });

    const isFlipped = document
      .querySelector('#invoice-generator-app .kz-calc-layout')
      ?.classList.contains('is-flipped');

    const data = {
      items,
      sellerLogo: sellerLogoDataUrl,
      paperSize: currentPaperSize,
      isFlipped: !!isFlipped,
      hiddenToggles
    };

    fields.forEach((f) => {
      const el = byId(f);
      if (el) data[f] = el.value;
    });

    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not persist invoice draft', e);
  }
}

function renderItemRows() {
  const tbody = byId('inv-items-body');
  if (!tbody) return;

  tbody.innerHTML = items
    .map((item, idx) => {
      const selectedUnit = item.unit || 'pcs';
      const unitOptions = UNITS.map(
        (u) =>
          `<option value="${u.id}" ${u.id === selectedUnit ? 'selected' : ''}>${isAr ? u.nameAr : u.nameEn}</option>`
      ).join('');

      return `
      <tr data-id="${item.id}">
        <td class="inv-cell-desc">
          <input type="text" class="kz-input kz-input-sm item-desc" value="${item.desc}" placeholder="${isAr ? 'اسم البند أو الخدمة...' : 'Item description...'}" style="width:100%;">
        </td>
        <td class="inv-cell-qty" style="width:90px; text-align:center;">
          <span class="inv-cell-field-label">${isAr ? 'الكمية والوحدة' : 'Qty & Unit'}</span>
          <input type="number" min="0.01" step="any" class="kz-input kz-input-sm item-qty" value="${item.qty}" style="width:100%; text-align:center;">
          <select class="item-unit-select" aria-label="${isAr ? 'الوحدة' : 'Unit'}">
            ${unitOptions}
          </select>
        </td>
        <td class="inv-cell-price" style="width:95px; text-align:center;">
          <span class="inv-cell-field-label">${isAr ? 'السعر' : 'Unit Price'}</span>
          <input type="number" min="0" step="any" class="kz-input kz-input-sm item-price" value="${item.price}" style="width:100%; text-align:center;">
        </td>
        <td class="inv-cell-total item-row-total" style="width:85px; text-align:center; font-weight:700; font-size:0.875rem;">
          <span class="inv-cell-total-label">${isAr ? 'الإجمالي:' : 'Total:'} </span>
          <span class="item-row-total-val">0.00</span>
        </td>
        <td class="inv-cell-actions" style="width:68px; text-align:center;">
          <div class="item-actions-cell">
            <button type="button" class="item-action-btn item-dup-btn" title="${isAr ? 'تكرار البند' : 'Duplicate'}" aria-label="Duplicate">⎘</button>
            <button type="button" class="item-action-btn is-delete item-del-btn" title="${isAr ? 'حذف البند' : 'Delete'}" aria-label="Delete">✕</button>
          </div>
        </td>
      </tr>
    `;
    })
    .join('');

  // Attach event listeners for each row
  tbody.querySelectorAll('.item-desc').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      items[i].desc = e.target.value;
      renderInvoicePreview();
      saveDraft();
    });
  });

  tbody.querySelectorAll('.item-qty').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      items[i].qty = Math.max(0, parseFloat(e.target.value) || 0);
      renderInvoicePreview();
      saveDraft();
    });
  });

  tbody.querySelectorAll('.item-unit-select').forEach((sel, i) => {
    sel.addEventListener('change', (e) => {
      items[i].unit = e.target.value;
      renderInvoicePreview();
      saveDraft();
    });
  });

  tbody.querySelectorAll('.item-price').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      items[i].price = Math.max(0, parseFloat(e.target.value) || 0);
      renderInvoicePreview();
      saveDraft();
    });
  });

  // Duplicate item
  tbody.querySelectorAll('.item-dup-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      const src = items[i];
      items.splice(i + 1, 0, {
        id: nextItemId++,
        desc: src.desc,
        unit: src.unit || 'pcs',
        qty: src.qty,
        price: src.price
      });
      renderItemRows();
      renderInvoicePreview();
      saveDraft();
    });
  });

  // Delete item
  tbody.querySelectorAll('.item-del-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      if (items.length <= 1) return;
      items.splice(i, 1);
      renderItemRows();
      renderInvoicePreview();
      saveDraft();
    });
  });
}

function renderInvoicePreview() {
  const currency = byId('inv-currency')?.value || 'SAR';

  // Check exclusion states
  const isVatExcluded = byId('inv-hide-vat-toggle')?.checked;
  const isDiscountExcluded = byId('inv-hide-discount-toggle')?.checked;

  const rawVatRate = parseFloat(byId('inv-vat-rate')?.value) || 0;
  const vatRate = isVatExcluded ? 0 : rawVatRate;

  const rawDiscountVal = parseFloat(byId('inv-discount-val')?.value) || 0;
  const discountVal = isDiscountExcluded ? 0 : rawDiscountVal;

  const discountType = byId('inv-discount-type')?.value || 'percent';

  let subtotal = Big(0);
  const itemRows = byId('inv-items-body')?.querySelectorAll('tr') || [];

  items.forEach((item, idx) => {
    const rowTotal = Big(item.qty || 0).times(Big(item.price || 0));
    subtotal = subtotal.plus(rowTotal);
    if (itemRows[idx]) {
      const cellVal = itemRows[idx].querySelector('.item-row-total-val') || itemRows[idx].querySelector('.item-row-total');
      if (cellVal) cellVal.textContent = rowTotal.toFixed(2);
    }
  });

  // Calculate discount
  let discountAmount = Big(0);
  if (!isDiscountExcluded) {
    if (discountType === 'percent') {
      discountAmount = subtotal.times(Big(discountVal).div(100));
    } else {
      discountAmount = Big(discountVal);
    }
    if (discountAmount.gt(subtotal)) discountAmount = subtotal;
  }

  const taxableAmount = subtotal.minus(discountAmount);
  const vatAmount = isVatExcluded ? Big(0) : taxableAmount.times(Big(vatRate).div(100));
  const grandTotal = taxableAmount.plus(vatAmount);

  // Render on Sheet: Seller Information
  if (byId('prev-seller-name')) {
    byId('prev-seller-name').textContent =
      byId('inv-seller-name')?.value || (isAr ? 'اسم الشركة / البائع' : 'Seller / Company Name');
  }
  if (byId('prev-seller-vat')) {
    byId('prev-seller-vat').textContent = byId('inv-seller-vat')?.value
      ? `${isAr ? 'الرقم الضريبي:' : 'Tax No:'} ${byId('inv-seller-vat').value}`
      : '';
  }
  if (byId('prev-seller-address')) {
    byId('prev-seller-address').textContent = byId('inv-seller-address')?.value || '';
  }
  if (byId('prev-seller-phone')) {
    byId('prev-seller-phone').textContent = byId('inv-seller-phone')?.value || '';
  }

  // Render on Sheet: Client Information
  if (byId('prev-client-name')) {
    byId('prev-client-name').textContent =
      byId('inv-client-name')?.value || (isAr ? 'اسم العميل' : 'Client Name');
  }
  if (byId('prev-client-vat')) {
    byId('prev-client-vat').textContent = byId('inv-client-vat')?.value
      ? `${isAr ? 'الرقم الضريبي للعميل:' : 'Client Tax No:'} ${byId('inv-client-vat').value}`
      : '';
  }
  if (byId('prev-client-address')) {
    byId('prev-client-address').textContent = byId('inv-client-address')?.value || '';
  }

  // Render on Sheet: Invoice Heading / Title
  if (byId('prev-inv-title')) {
    const customTitle = byId('inv-title')?.value?.trim();
    byId('prev-inv-title').textContent =
      customTitle !== undefined && customTitle !== ''
        ? customTitle
        : (isAr ? 'فاتورة' : 'INVOICE');
  }

  // Render on Sheet: Invoice Meta
  if (byId('prev-inv-num')) {
    byId('prev-inv-num').textContent = byId('inv-number')?.value || 'INV-001';
  }
  if (byId('prev-inv-date')) {
    byId('prev-inv-date').textContent = byId('inv-date')?.value || '';
  }
  if (byId('prev-inv-due')) {
    byId('prev-inv-due').textContent = byId('inv-due-date')?.value || '';
  }

  // Logo
  const prevLogo = byId('prev-logo-img');
  if (prevLogo) {
    if (sellerLogoDataUrl) {
      prevLogo.src = sellerLogoDataUrl;
      prevLogo.style.display = 'block';
    } else {
      prevLogo.style.display = 'none';
    }
  }

  // Items table in preview
  const prevItemsTbody = byId('prev-items-body');
  if (prevItemsTbody) {
    prevItemsTbody.innerHTML = items
      .map((it, i) => {
        const u = UNITS.find((unit) => unit.id === it.unit);
        const unitName = u && u.id !== 'none' ? (isAr ? u.nameAr : u.nameEn) : '';
        const qtyDisplay = unitName ? `${it.qty} ${unitName}` : `${it.qty}`;
        const rowTotal = Big(it.qty || 0).times(Big(it.price || 0));

        return `
        <tr>
          <td style="padding:8px 12px; border-bottom:1px solid #cbd5e1;">${i + 1}</td>
          <td style="padding:8px 12px; border-bottom:1px solid #cbd5e1;">
            <strong>${it.desc || '-'}</strong>
          </td>
          <td style="padding:8px 12px; text-align:center; border-bottom:1px solid #cbd5e1;">${qtyDisplay}</td>
          <td style="padding:8px 12px; text-align:end; border-bottom:1px solid #cbd5e1;">${formatNumber(it.price)}</td>
          <td style="padding:8px 12px; text-align:end; font-weight:700; border-bottom:1px solid #cbd5e1;">${formatNumber(rowTotal.toFixed(2))}</td>
        </tr>
      `;
      })
      .join('');
  }

  // Totals in preview
  if (byId('prev-subtotal')) {
    byId('prev-subtotal').textContent = formatInvoiceAmount(subtotal.toFixed(2), currency);
  }
  if (byId('prev-discount')) {
    byId('prev-discount').textContent = formatInvoiceAmount(discountAmount.toFixed(2), currency);
  }
  if (byId('prev-vat')) {
    byId('prev-vat').textContent = formatInvoiceAmount(vatAmount.toFixed(2), currency);
  }
  if (byId('prev-grand-total')) {
    byId('prev-grand-total').textContent = formatInvoiceAmount(grandTotal.toFixed(2), currency);
  }

  // Update live mobile navigation & button total badges
  const formattedLiveTotal = formatInvoiceAmount(grandTotal.toFixed(2), currency);
  const liveTotalTab = byId('inv-nav-live-total');
  if (liveTotalTab) liveTotalTab.textContent = formattedLiveTotal;
  const mobileActionTotal = byId('inv-mobile-action-total');
  if (mobileActionTotal) mobileActionTotal.textContent = formattedLiveTotal;

  // Universal Tafgeet (amount in words)
  const wordsEl = byId('prev-total-words');
  if (wordsEl) {
    wordsEl.textContent = getInvoiceTafgeet(grandTotal.toFixed(2), currency);
  }

  // Notes
  if (byId('prev-notes')) {
    byId('prev-notes').textContent = byId('inv-notes')?.value || '';
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInvoiceGenerator);
  } else {
    initInvoiceGenerator();
  }
}
